import React, { useEffect, useState } from "react";
import "swiper/css";
import "swiper/css/free-mode";
import "swiper/css/navigation";
import "swiper/css/thumbs";
import { FreeMode, Navigation, Thumbs } from "swiper/modules";
import { Swiper, SwiperSlide } from "swiper/react";
import { useProductData } from "@/zustand/productStore.js";
import glideImage from "@/func/glideImage";

const SwiperGallery = () => {
  const [thumbsSwiper, setThumbsSwiper] = useState(null);
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 1071);
  const productData = useProductData();
  const heroContent = window.post_template.checkout_v4_product_hero;
  const useNewGallery = heroContent.checkout_v4_product_hero_image_toggle;
  const images = useNewGallery
    ? heroContent.checkout_v4_product_hero_image_slider_gallery_new.map((item, index) => ({
        original: glideImage(item.gallery_image, { w: 500 }),
        mobile: glideImage(item.gallery_image, { w: 420 }),
        video_id: item.video_id,
        index,
        thumbnail: item.video_thumb || glideImage(item.gallery_image, { w: 150 }),
        useVideo: item.use_video,
        autoplay: item.video_autofplay,
        loop: item.video_loop,
      }))
    : heroContent.checkout_v4_product_hero_image_slider_gallery.map((desktopItem, index) => {
        const mobileItem = heroContent.checkout_v4_product_hero_image_slider_gallery_mobile[index] || {}; // Match mobile by index
        return {
          original: glideImage(desktopItem.url, { w: 500 }),
          thumbnail: glideImage(desktopItem.url, { w: 150 }),
          mobile: glideImage(desktopItem.url, { w: 420 }),
        };
      });

  // change src of iframe to next slide if its video
  const handleSlideChange = (e) => {
    const nextSlide = e.realIndex + 1;
    const iframe = document.getElementById(`video_player-${nextSlide}`);
    if (!iframe || !iframe.dataset.src) return;
    iframe.src = iframe.dataset.src;
    iframe.removeAttribute("data-src");
  };

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 1071);
    };

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const NextButton = () => (
    <svg xmlns="http://www.w3.org/2000/svg" width={32} height={32} style={{ transform: "rotate(180deg)" }} fill="none">
      <path
        stroke="#333"
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={2}
        d="M18.667 9.333 12 16l6.667 6.667"
      />
    </svg>
  );

  const PrevButton = () => (
    <svg xmlns="http://www.w3.org/2000/svg" width={32} height={32} fill="none">
      <path
        stroke="#333"
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={2}
        d="M18.667 9.333 12 16l6.667 6.667"
      />
    </svg>
  );

  return (
    <div
      className={`hero-swiper-container ${productData?.show_product_picker_v2 && "hero-swiper-container__productPickerV2"}`}
    >
      {/* Main Swiper */}
      <Swiper
        style={{
          width: isMobile ? "100%" : "calc(100% - 90px)",
          height: "100%",
          borderRadius: "10px",
          marginLeft: "0",
          paddingBottom: "0",
          order: isMobile ? 1 : 2,
        }}
        onSlideChange={handleSlideChange}
        spaceBetween={10}
        navigation={{
          nextEl: ".swiper-button-next",
          prevEl: ".swiper-button-prev",
        }}
        loop={true}
        thumbs={{ swiper: thumbsSwiper }}
        modules={[FreeMode, Navigation, Thumbs]}
        className="main-swiper"
      >
        {images.map((item, index) => (
          <SwiperSlide key={`main-${index}`}>
            {item.useVideo ? (
              <div className="hero-gallery__videoWrapper">
                <div className="hero-gallery__video">
                  <iframe
                    id={`video_player-${item.index}`}
                    className="content__image-video"
                    data-src={
                      index !== 0
                        ? `https://iframe.videodelivery.net/${item.video_id}/iframe/?controls=${!item.autoplay}&autoplay=${item.autoplay}&loop=${item.loop}&muted=${item.autoplay}`
                        : ""
                    }
                    src={
                      index === 0
                        ? `https://iframe.videodelivery.net/${item.video_id}/iframe/?controls=${!item.autoplay}&autoplay=${item.autoplay}&loop=${item.loop}&muted=${item.autoplay}`
                        : ""
                    }
                    style={{
                      position: "absolute",
                      height: "100%",
                      width: "100%",
                      top: "50%",
                      left: "50%",
                      transform: "scale(1.01) translate(-50%, -50%)",
                      borderRadius: "10px",
                    }}
                    allow="accelerometer; gyroscope; autoplay; encrypted-media; picture-in-picture;"
                    allowFullScreen={true}
                  ></iframe>
                </div>
              </div>
            ) : (
              <picture>
                <source srcset={item.original} media="(min-width: 678px)" />
                <source srcset={item.mobile} media="(max-width: 677px)" />
                <img
                  src={item.original}
                  alt={`Slide ${index + 1}`}
                  style={{ width: "100%", height: "100%", objectFit: "fill" }}
                  loading={index === 0 ? "eager" : "lazy"}
                  className="mon-img-500-w"
                />
              </picture>
            )}
          </SwiperSlide>
        ))}

        <div className="swiper-button-prev">
          <PrevButton />
        </div>
        <div className="swiper-button-next">
          <NextButton />
        </div>
      </Swiper>

      {/*Thumbnail Swiper*/}
      <Swiper
        onSwiper={setThumbsSwiper}
        spaceBetween={10}
        direction={isMobile ? "horizontal" : "vertical"}
        style={{
          width: isMobile ? "100%" : "80px",
          height: "100%",
          marginLeft: "0",
          marginRight: isMobile ? "0" : "10px",
          marginTop: isMobile ? "10px" : "0",
          paddingBottom: "0",
          order: isMobile ? 2 : 1,
        }}
        loop={true}
        breakpoints={{
          0: {
            slidesPerView: 5,
          },
          540: {
            slidesPerView: 7,
          },
          767: {
            slidesPerView: 5,
          },
        }}
        slidesPerView={5}
        freeMode={true}
        watchSlidesProgress={true}
        modules={[FreeMode, Navigation, Thumbs]}
        className="thumbs-swiper"
      >
        {images.map((img, index) => (
          <SwiperSlide key={`thumb-${index}`} style={{ overflow: "hidden", borderRadius: "4px" }}>
            <img
              src={img.thumbnail}
              alt={`Thumbnail ${index + 1}`}
              style={{ width: "100%", height: "100%", objectFit: "cover" }}
              className="mon-img-150-w"
            />
          </SwiperSlide>
        ))}
      </Swiper>
    </div>
  );
};

export default SwiperGallery;
